import * as React from 'react';
import Link from 'next/link';
import { useSelector } from 'react-redux';
import useSWR from 'swr';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import axios from 'axios';

import config from 'config';
import { ReduxState } from 'ducks';
import { getThemeCssOverride } from 'components/Themes';
import { CardSlider } from 'components/PrivateMarketplace/CardSlider/CardSlider';
import { BookingWidgetThemeContext } from 'contexts/BookingWidgetThemeContext';
import { appendQueryString } from 'lib/util/appendQueryString';
import { useQueryString } from 'hooks/useQueryString';
import { ProductCard } from 'components/PrivateMarketplace/ProductCard/ProductCard';
import { toProductSummaryShape, ProductSummaryShape } from 'lib/util/productSummaryShape';
import { ProductSummary } from 'models/product';
import { ApiKeyContext } from 'contexts/ApiKeyContext';
import { selectIsPMP } from 'lib/util/privateMarketplace';
import { CardSlider as PMPCardSlider } from 'components/PrivateMarketplace/CardSlider/CardSlider';
import { ProductSummaryCard } from 'components/ProductSummaryCard/ProductSummaryCard';
import { getAnnualPassTitle } from 'lib/util/util';

const fetcher = (url: string, apiKey: string, contentLanguage: string) =>
  axios
    .get(url, {
      headers: { 'x-api-key': apiKey, 'accept-language': contentLanguage },
    })
    .then((response) => response.data.products);

export const AnnualPassRecommendationProducts = () => {
  const product = useSelector((state: ReduxState) => state.server.product.product);
  const bookingWidgetTheme = React.useContext(BookingWidgetThemeContext);
  const queryString = useQueryString();
  const { t, i18n } = useTranslation();
  const recommendedProductIds = product?.annual_pass_mode_settings?.recommended_product_ids || [];
  const shouldFetchProductSummaries = recommendedProductIds.length > 0;
  const { apiKey } = React.useContext(ApiKeyContext);
  const isPMP = useSelector(selectIsPMP);

  const locale = i18n.language;

  const { data } = useSWR<ProductSummary[]>(
    shouldFetchProductSummaries ? [`${config.apiUrl}/products`, apiKey, locale] : null,
    fetcher
  );

  const productSummaries = data?.map?.(toProductSummaryShape);

  const recommendedProducts: ProductSummaryShape[] = [];
  recommendedProductIds.forEach((productId) => {
    const product = productSummaries?.find((p) => p.id === productId);
    if (product) {
      recommendedProducts.push(product);
    }
  });

  if (recommendedProducts.length === 0) {
    return null;
  }

  return (
    <div className={clsx('base-related', getThemeCssOverride(bookingWidgetTheme, 'base-related'))}>
      <h3 className="base-related__headline">
        {t('Recommended {{title}}', { title: getAnnualPassTitle(product, t('Annual Pass')) })}
      </h3>
      {isPMP ? (
        <PMPCardSlider
          centerNarrowContent={true}
          data={recommendedProducts}
          renderElement={(product) => (
            <Link href={appendQueryString(`/top/products/${product.id}`, queryString)}>
              <ProductCard displayLabelTypes={['PROMOTION']} product={product} />
            </Link>
          )}
        />
      ) : (
        <CardSlider
          centerNarrowContent={true}
          data={recommendedProducts}
          renderElement={(product) =>
            product ? <ProductSummaryCard product={product} orientation="VERTICAL" /> : null
          }
        />
      )}
    </div>
  );
};
